import { useEffect, useMemo, useState } from 'react';
import { SearchIcon, PencilIcon, TrashIcon, PlusIcon, ChevronDownIcon } from '@bizfirst/common-react';
import type { AcceptedCredentialType, CredentialDTO, CredentialTypeDTO } from '../../types';
import { useCredentialAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { CredentialEditor } from './CredentialEditor';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

export interface CredentialPickerProps {
  value?: number | null;
  onChange: (credentialId: number | null, typeCode?: string) => void;
  /**
   * Optional type filter. Absent → search-all mode (flow-studio's old CredentialSelector
   * behavior). Present → type-aware mode (flow-studio's old CredentialSection behavior); the Type
   * dropdown is hidden and auto-selected when exactly one type is accepted.
   */
  acceptedCredentialTypes?: AcceptedCredentialType[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  allowManage?: boolean;
}

/**
 * Merges flow-studio-designer's CredentialSelector.tsx (type-agnostic search picker) and
 * CredentialSection.tsx (type-aware picker) into one component with an optional
 * `acceptedCredentialTypes` prop, per octopus-admin-spec/architecture.md's explicit merge
 * instruction — these were two components in Flow Studio for what is fundamentally one concern
 * with an optional filter. Also fixes a confirmed bug from the source: CredentialSelector's own
 * type list for its "pick a type before creating" flow was a hardcoded 4-entry array (a `TODO`
 * left in that file) instead of calling the real credential-types endpoint — this component
 * always calls `getCredentialTypes()` for real.
 */
export function CredentialPicker({ value, onChange, acceptedCredentialTypes, label = 'Credential', placeholder = 'Search credentials...', disabled, allowManage = true }: CredentialPickerProps) {
  const api = useCredentialAdminApiClient();
  const typeAware = !!acceptedCredentialTypes && acceptedCredentialTypes.length > 0;
  const [allTypes, setAllTypes] = useState<CredentialTypeDTO[]>([]);
  const [selectedTypeCode, setSelectedTypeCode] = useState<string | null>(typeAware && acceptedCredentialTypes!.length === 1 ? acceptedCredentialTypes![0].code : null);
  const [credentials, setCredentials] = useState<CredentialDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [editorState, setEditorState] = useState<{ mode: 'create' | 'edit'; credentialId?: number } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CredentialDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.getCredentialTypes().then((result) => setAllTypes(result.data ?? []));
  }, [api]);

  const acceptedTypeOptions = useMemo(() => {
    if (!typeAware) return [];
    const codes = new Set(acceptedCredentialTypes!.map((t) => t.code));
    return allTypes.filter((t) => codes.has(t.Code));
  }, [typeAware, acceptedCredentialTypes, allTypes]);

  const selectedCredential = useMemo(() => credentials.find((c) => c.CredentialID === value) ?? null, [credentials, value]);

  const loadCredentials = async (openAfter = true) => {
    setLoading(true);
    const result = typeAware && selectedTypeCode ? await api.getByTypeCode(selectedTypeCode) : await api.getActive();
    setCredentials(result.data ?? []);
    setLoading(false);
    if (openAfter) setShowResults(true);
  };

  useEffect(() => {
    if (typeAware && !selectedTypeCode) return; // waiting on a type selection
    // Initial/type-driven load must NOT open the dropdown — it's populating the list so a
    // preselected value's name can resolve, not a user-initiated browse.
    loadCredentials(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeCode]);

  const filtered = search.trim() ? credentials.filter((c) => c.Name.toLowerCase().includes(search.toLowerCase())) : credentials;

  const handleSelect = (credential: CredentialDTO) => {
    const typeCode = allTypes.find((t) => t.CredentialTypeID === credential.CredentialTypeID)?.Code;
    onChange(credential.CredentialID, typeCode);
    setSearch(credential.Name);
    setShowResults(false);
  };

  const handleEditorSaved = async (credential: CredentialDTO) => {
    await loadCredentials();
    setEditorState(null);
    const typeCode = allTypes.find((t) => t.CredentialTypeID === credential.CredentialTypeID)?.Code;
    onChange(credential.CredentialID, typeCode);
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteById(pendingDelete.CredentialID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      await loadCredentials();
      if (value === pendingDelete.CredentialID) onChange(null);
    }
  };

  // Deliberately does NOT fall back to acceptedTypeOptions[0] when nothing is selected yet — that
  // silently created e.g. an API_KEY credential when the user clicked "Create" on a multi-type
  // node (Odoo) without first choosing a type from the dropdown above. Leaving this undefined
  // instead makes CredentialEditor fall into its own 'selectType' step (allowedCredentialTypes
  // below is the full acceptedTypeOptions list), showing the type-picker list the user expected.
  const createDefaultTypeCode = typeAware ? selectedTypeCode ?? undefined : undefined;

  // When there's more than one accepted type, the type <select> and the search row are grouped
  // into one bordered control (.aaa-entity-picker-group, see aiAgentAdmin.css) instead of each
  // keeping its own independent border — otherwise they read as two unrelated floating inputs
  // stacked on top of each other rather than one "pick type, then pick credential" control
  // (reported live: "displaying two UIs for both credential type and credentials").
  const showTypeRow = typeAware && acceptedCredentialTypes!.length > 1;

  const searchRow = (
    <div className="aaa-entity-search-row">
      <div className="aaa-entity-search-input-wrap">
        <SearchIcon size={14} />
        <input
          type="text"
          placeholder={placeholder}
          value={search || selectedCredential?.Name || ''}
          disabled={disabled || (typeAware && !selectedTypeCode)}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
        />
      </div>
      <button type="button" className="aaa-icon-btn aaa-entity-browse-btn" onClick={() => (showResults ? setShowResults(false) : loadCredentials())} disabled={disabled} title="Browse credentials">
        <ChevronDownIcon size={14} />
      </button>
      {allowManage && (
        <>
          <button type="button" className="aaa-icon-btn" disabled={disabled || !value} onClick={() => value && setEditorState({ mode: 'edit', credentialId: value })} title="Edit credential">
            <PencilIcon size={13} />
          </button>
          <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" disabled={disabled || !value} onClick={() => selectedCredential && setPendingDelete(selectedCredential)} title="Delete credential">
            <TrashIcon size={13} />
          </button>
          <button type="button" className="aaa-entity-create-btn" disabled={disabled || (typeAware && acceptedTypeOptions.length === 0)} onClick={() => setEditorState({ mode: 'create' })} title="Add new credential">
            <PlusIcon size={13} /> Create
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="aaa-entity-picker">
      <label className="aaa-entity-label">{label}</label>

      {showTypeRow ? (
        <div className="aaa-entity-picker-group">
          <div className="aaa-entity-type-row">
            <span className="aaa-entity-type-tag">Type</span>
            <select value={selectedTypeCode ?? ''} disabled={disabled} onChange={(e) => setSelectedTypeCode(e.target.value || null)}>
              <option value="">— Select credential type —</option>
              {acceptedTypeOptions.map((t) => (
                <option key={t.CredentialTypeID} value={t.Code}>
                  {t.Name}
                </option>
              ))}
            </select>
          </div>
          {searchRow}
        </div>
      ) : (
        searchRow
      )}

      {showResults && (
        <div className="aaa-entity-dropdown">
          {loading && <div className="aaa-entity-dropdown-empty">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="aaa-entity-dropdown-empty">No credentials found.</div>}
          {!loading &&
            filtered.map((credential) => (
              <button type="button" key={credential.CredentialID} className={`aaa-entity-dropdown-item${credential.CredentialID === value ? ' selected' : ''}`} onClick={() => handleSelect(credential)}>
                <span>{credential.Name}</span>
              </button>
            ))}
        </div>
      )}

      {editorState && (
        <CredentialEditor
          credentialId={editorState.mode === 'edit' ? editorState.credentialId : undefined}
          defaultCredentialTypeCode={editorState.mode === 'create' ? createDefaultTypeCode : undefined}
          acceptedCredentialType={
            typeAware
              ? acceptedCredentialTypes!.find((t) => t.code === (editorState.mode === 'edit' ? selectedTypeCode : createDefaultTypeCode))
              : undefined
          }
          allowedCredentialTypes={typeAware ? acceptedTypeOptions : allTypes}
          onClose={() => setEditorState(null)}
          onSaved={handleEditorSaved}
        />
      )}

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Credential"
        message={`Delete "${pendingDelete?.Name}"? This soft-deletes the credential — anything still referencing it will fail to resolve.`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
